import React from 'react';
import { StaticPageHeader } from '../components/StaticPageHeader';
import { Footer } from '../components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Briefcase, Users, TrendingUp, Heart, Clock, MapPin } from 'lucide-react';

export const Careers = () => {
  return (
    <div className="min-h-screen bg-white">
      <StaticPageHeader />
      
      {/* Add padding top to account for fixed header */}
      <div className="pt-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-full p-4">
                  <Briefcase className="w-12 h-12" />
                </div>
              </div>
              <h1 className="text-5xl font-bold mb-4">Join Our Team</h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Help us drive HR forward for growing businesses. We're building the future of accessible, expert HR guidance.
              </p>
            </div>
          </div>
        </div>

        {/* Why Work With Us Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why DriveHR?</h2>
            <p className="text-xl text-gray-600">
              We're on a mission to make professional HR accessible to every growing business
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <Card>
              <CardHeader>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <CardTitle>Collaborative Culture</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Work with a passionate team of HR professionals and tech innovators who value your input and expertise.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="w-6 h-6 text-yellow-600" />
                </div>
                <CardTitle>Growth Opportunities</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Continuous learning, professional development, and clear career advancement paths for motivated team members.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <Heart className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle>Work-Life Balance</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Flexible schedules, remote work options, and a genuine commitment to your well-being and happiness.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Benefits Section */}
          <div className="bg-gray-50 rounded-2xl p-8 mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Our Benefits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Competitive Compensation</h4>
                  <p className="text-gray-600 text-sm">Market-rate salaries plus performance bonuses</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Health & Wellness</h4>
                  <p className="text-gray-600 text-sm">Comprehensive health, dental, and vision coverage</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Flexible Time Off</h4>
                  <p className="text-gray-600 text-sm">Generous PTO, sick leave, and paid holidays</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Professional Development</h4>
                  <p className="text-gray-600 text-sm">Training budget, conference attendance, and certifications</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Remote Work</h4>
                  <p className="text-gray-600 text-sm">Work from anywhere with flexible scheduling options</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">401(k) Matching</h4>
                  <p className="text-gray-600 text-sm">Retirement savings plan with company matching</p>
                </div>
              </div>
            </div>
          </div>

          {/* Open Positions Section */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Open Positions</h3>
            
            <div className="space-y-4">
              {/* Position 1 */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">Senior HR Consultant</CardTitle>
                      <CardDescription className="flex flex-wrap gap-4 text-base">
                        <span className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          Full-Time
                        </span>
                        <span className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          Remote
                        </span>
                      </CardDescription>
                    </div>
                    <Button className="bg-blue-600 hover:bg-blue-700">Apply Now</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    Lead client engagements, develop HR strategies, and provide expert guidance to growing businesses. 5+ years of HR experience required, SHRM certification preferred.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">HR Strategy</span>
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">SHRM Certified</span>
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">Client Facing</span>
                  </div>
                </CardContent>
              </Card>

              {/* Position 2 */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">Content Writer - HR Resources</CardTitle>
                      <CardDescription className="flex flex-wrap gap-4 text-base">
                        <span className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          Full-Time
                        </span>
                        <span className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          Remote
                        </span>
                      </CardDescription>
                    </div>
                    <Button className="bg-blue-600 hover:bg-blue-700">Apply Now</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    Create compelling HR content, templates, and guides for small businesses. Strong writing skills and HR knowledge required. Experience in content marketing a plus.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full">Content Writing</span>
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full">HR Knowledge</span>
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full">SEO</span>
                  </div>
                </CardContent>
              </Card>

              {/* Position 3 */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">Customer Success Manager</CardTitle>
                      <CardDescription className="flex flex-wrap gap-4 text-base">
                        <span className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          Full-Time
                        </span>
                        <span className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          Remote
                        </span>
                      </CardDescription>
                    </div>
                    <Button className="bg-blue-600 hover:bg-blue-700">Apply Now</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    Build strong relationships with clients, ensure product adoption, and drive customer satisfaction. 3+ years in customer success or account management preferred.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">Customer Success</span>
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">Relationship Building</span>
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">SaaS Experience</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-12 text-center text-white">
            <h3 className="text-3xl font-bold mb-4">Don't See Your Role?</h3>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              We're always looking for talented individuals to join our team. Send us your resume and tell us how you can contribute to DriveHR's mission.
            </p>
            <Button 
              size="lg" 
              className="bg-white text-blue-600 hover:bg-gray-100 font-semibold"
              onClick={() => window.location.href = '#contact'}
            >
              Get In Touch
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};
